/**
 * ProjectSelector — the cascading Space → Project scope control of the
 * unified header (specs/changes/workspace-organization, scope-selector;
 * extends enterprise-project-organization's project-selector-surfaces).
 *
 * Presentational: data and selection state come from the app's ProjectContext
 * so this stays consumable by every React surface. Renders a trigger pill
 * (icon-only below `sm`, mirroring how SearchBar degrades) opening a panel
 * with an optional space row ("All spaces" + each visible space) above the
 * filter input and the visible projects under an "All projects" entry.
 * The space tier renders only when the caller can see > 1 space — pass
 * `hasSpaceTier` the same list to decide — so single-space tenants keep
 * exactly the project-only selector. Callers hide the whole control when both
 * lists are dormant (no projects, ≤ 1 space) — rendering it then is a caller
 * bug, not a supported state.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, FolderKanban } from 'lucide-react';
import { cn } from '@systemdox/utils';
import type { SelectorProject, SelectorSpace } from '@systemdox/types';

/** The space tier exists only when the caller can see more than one space —
 *  with ≤ 1 there is nothing to switch between (scope-selector invariant). */
export function hasSpaceTier(spaces: readonly SelectorSpace[] | undefined): boolean {
  return (spaces?.length ?? 0) > 1;
}

export interface ProjectSelectorProps {
  projects: SelectorProject[];
  selected: SelectorProject | null;
  onSelect: (project: SelectorProject | null) => void;
  /** Called as the filter input changes — hook for server-side type-ahead. */
  onQueryChange?: (query: string) => void;
  /** Total visible pre-page. When > projects.length the panel type-aheads
   *  via `onSearch` instead of filtering locally (task 13 — >1k projects). */
  total?: number;
  /** Server-side name-prefix search; required when `total` exceeds the page. */
  onSearch?: (query: string) => Promise<SelectorProject[]>;
  /** Visible (unarchived) spaces — the cascade's upper tier, hidden when ≤ 1. */
  spaces?: SelectorSpace[];
  selectedSpace?: SelectorSpace | null;
  /** Select a space (null = All spaces). The caller owns the invariants:
   *  narrowing the project list and clearing a foreign project selection. */
  onSelectSpace?: (space: SelectorSpace | null) => void;
  loading?: boolean;
  className?: string;
}

/** Hard render cap: past this the footer tells the user to keep typing —
 *  a thousand-row listbox helps nobody and janks the panel. */
const RENDER_CAP = 100;

export function ProjectSelector({
  projects,
  selected,
  onSelect,
  onQueryChange,
  total,
  onSearch,
  spaces = [],
  selectedSpace = null,
  onSelectSpace,
  loading = false,
  className,
}: ProjectSelectorProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [serverMatches, setServerMatches] = useState<SelectorProject[] | null>(null);
  const [searching, setSearching] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The loaded page is complete when it holds everything visible; otherwise
  // local filtering would silently miss projects and the panel must ask the
  // server (debounced) as the user types.
  const pageIsComplete = total === undefined || total <= projects.length;

  useEffect(() => {
    if (pageIsComplete || !onSearch) return;
    const q = query.trim();
    if (!q) {
      setServerMatches(null);
      return;
    }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      const matches = await onSearch(q.toLowerCase());
      setServerMatches(matches);
      setSearching(false);
    }, 200);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [query, pageIsComplete, onSearch]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!pageIsComplete && serverMatches !== null && q) return serverMatches;
    return q ? projects.filter((p) => p.name.toLowerCase().includes(q)) : projects;
  }, [projects, query, pageIsComplete, serverMatches]);

  const rendered = filtered.slice(0, RENDER_CAP);
  const truncated = filtered.length > RENDER_CAP || (!pageIsComplete && !query.trim());

  // "All projects" occupies index 0; projects follow.
  const optionCount = rendered.length + 1;

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    function onDocMouseDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  useEffect(() => setActiveIndex(0), [query, open]);

  function choose(index: number) {
    onSelect(index === 0 ? null : rendered[index - 1]);
    setOpen(false);
    setQuery('');
    setServerMatches(null);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, optionCount - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(activeIndex);
    }
  }

  // The space tier renders only past one visible space; the trigger then
  // reads `Space / Project` when both tiers are set.
  const showSpaceTier = hasSpaceTier(spaces);
  const spaceLabel = showSpaceTier ? selectedSpace?.name ?? null : null;
  const triggerLabel = selected
    ? spaceLabel
      ? `${spaceLabel} / ${selected.name}`
      : selected.name
    : spaceLabel ?? 'All projects';

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={triggerLabel}
        className={cn(
          'flex h-9 items-center gap-1.5 rounded-md px-2 text-sm font-medium transition-colors',
          'text-slate-500 hover:bg-slate-100 hover:text-slate-700',
          'dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200',
          (selected || spaceLabel) && 'text-slate-700 dark:text-slate-200',
        )}
      >
        <FolderKanban className="h-4 w-4 shrink-0" />
        <span className="hidden max-w-[12rem] truncate sm:block">{triggerLabel}</span>
        <ChevronDown className="hidden h-3.5 w-3.5 shrink-0 sm:block" />
      </button>

      {open && (
        <div
          className="absolute right-0 z-50 mt-1 w-72 rounded-lg border border-slate-200 bg-white p-1.5 shadow-lg dark:border-slate-700 dark:bg-slate-900"
          onKeyDown={onKeyDown}
        >
          {showSpaceTier && (
            <div
              role="group"
              aria-label="Spaces"
              className="mb-1 flex flex-wrap gap-1 border-b border-slate-100 pb-1.5 dark:border-slate-800"
            >
              <button
                type="button"
                aria-pressed={selectedSpace === null}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSelectSpace?.(null);
                }}
                className={cn(
                  'rounded-md px-2 py-1 text-xs font-medium transition-colors',
                  selectedSpace === null
                    ? 'bg-slate-200 text-slate-900 dark:bg-slate-700 dark:text-slate-100'
                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200',
                )}
              >
                All spaces
              </button>
              {spaces.map((space) => (
                <button
                  key={space.spaceId}
                  type="button"
                  aria-pressed={selectedSpace?.spaceId === space.spaceId}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSelectSpace?.(space);
                  }}
                  className={cn(
                    'max-w-full truncate rounded-md px-2 py-1 text-xs font-medium transition-colors',
                    selectedSpace?.spaceId === space.spaceId
                      ? 'bg-slate-200 text-slate-900 dark:bg-slate-700 dark:text-slate-100'
                      : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200',
                  )}
                >
                  {space.name}
                </button>
              ))}
            </div>
          )}
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              onQueryChange?.(e.target.value);
            }}
            placeholder="Find a project…"
            role="combobox"
            aria-expanded="true"
            aria-controls="sdx-project-listbox"
            className="mb-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
          />
          <ul
            id="sdx-project-listbox"
            role="listbox"
            aria-label="Projects"
            className="max-h-64 overflow-y-auto"
          >
            <li
              role="option"
              aria-selected={selected === null}
              onMouseEnter={() => setActiveIndex(0)}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(0);
              }}
              className={cn(
                'cursor-pointer rounded-md px-2 py-1.5 text-sm',
                activeIndex === 0 && 'bg-slate-100 dark:bg-slate-800',
                selected === null
                  ? 'font-semibold text-slate-900 dark:text-slate-100'
                  : 'text-slate-600 dark:text-slate-300',
              )}
            >
              All projects
            </li>
            {rendered.map((project, i) => (
              <li
                key={project.projectId}
                role="option"
                aria-selected={selected?.projectId === project.projectId}
                onMouseEnter={() => setActiveIndex(i + 1)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(i + 1);
                }}
                className={cn(
                  'flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5 text-sm',
                  activeIndex === i + 1 && 'bg-slate-100 dark:bg-slate-800',
                  selected?.projectId === project.projectId
                    ? 'font-semibold text-slate-900 dark:text-slate-100'
                    : 'text-slate-600 dark:text-slate-300',
                )}
              >
                <span className="truncate">{project.name}</span>
                <span className="ml-2 shrink-0 text-xs text-slate-400">
                  {project.repoCount}
                </span>
              </li>
            ))}
            {rendered.length === 0 && (
              <li className="px-2 py-1.5 text-sm text-slate-400">
                {loading || searching ? 'Loading…' : 'No matching projects'}
              </li>
            )}
            {truncated && rendered.length > 0 && (
              <li aria-hidden className="px-2 py-1.5 text-xs text-slate-400">
                Showing {rendered.length} of {total ?? filtered.length} — keep typing to narrow
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
